import { alpha, Components, Theme } from "@mui/material/styles";

const components: Components<Theme> = {
  MuiCssBaseline: {
    // global style overrides
    styleOverrides: (theme) => ({
      a: {
        color: theme.palette.primary.main,
      },
    }),
  },

  MuiAlertTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.alertTitle,
      }),
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      popupIndicator: {
        "&:hover": {
          backgroundColor: "unset",
        },

        ".MuiTouchRipple-root": {
          display: "none",
        },
      },
    },
  },

  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },

    styleOverrides: {
      root: {
        textTransform: "none",
        minWidth: "unset",
      },

      sizeLarge: ({ theme }) => ({
        ...theme.typography.buttonLarge,
      }),

      sizeMedium: ({ theme }) => ({
        ...theme.typography.button,
      }),

      sizeSmall: ({ theme }) => ({
        ...theme.typography.buttonSmall,
      }),
    },
  },

  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        textTransform: "none",
        minWidth: "unset",
        "&.MuiToggleButton-primary": {
          color: theme.palette.primary.main,
          borderColor: alpha(theme.palette.primary.main, 0.5),
          "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(
              theme.palette.primary.main,
              theme.palette.action.hoverOpacity,
            ),
          },
          "&.Mui-selected": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              borderColor: theme.palette.primary.dark,
            },
          },
        },
        "&.MuiToggleButton-secondary": {
          color: theme.palette.secondary.main,
          borderColor: alpha(theme.palette.secondary.main, 0.5),
          "&:hover": {
            borderColor: theme.palette.secondary.main,
            backgroundColor: alpha(
              theme.palette.secondary.main,
              theme.palette.action.hoverOpacity,
            ),
          },
          "&.Mui-selected": {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            borderColor: theme.palette.secondary.main,
            "&:hover": {
              backgroundColor: theme.palette.secondary.dark,
              borderColor: theme.palette.secondary.dark,
            },
          },
        },
        "&.MuiToggleButton-root.Mui-error": {
          color: theme.palette.error.main,
          borderColor: alpha(theme.palette.error.main, 0.5),
          "&:hover": {
            borderColor: theme.palette.error.main,
            backgroundColor: alpha(
              theme.palette.error.main,
              theme.palette.action.hoverOpacity,
            ),
          },
          "&.Mui-selected": {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
            borderColor: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.dark,
              borderColor: theme.palette.error.dark,
            },
          },
        },
        "&.MuiToggleButton-warning": {
          color: theme.palette.warning.main,
          borderColor: alpha(theme.palette.warning.main, 0.5),
          "&:hover": {
            borderColor: theme.palette.warning.main,
            backgroundColor: alpha(
              theme.palette.warning.main,
              theme.palette.action.hoverOpacity,
            ),
          },
          "&.Mui-selected": {
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
            borderColor: theme.palette.warning.main,
            "&:hover": {
              backgroundColor: theme.palette.warning.dark,
              borderColor: theme.palette.warning.dark,
            },
          },
        },
        "&.MuiToggleButton-info": {
          color: theme.palette.info.main,
          borderColor: alpha(theme.palette.info.main, 0.5),
          "&:hover": {
            borderColor: theme.palette.info.main,
            backgroundColor: alpha(
              theme.palette.info.main,
              theme.palette.action.hoverOpacity,
            ),
          },
          "&.Mui-selected": {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.info.contrastText,
            borderColor: theme.palette.info.main,
            "&:hover": {
              backgroundColor: theme.palette.info.dark,
              borderColor: theme.palette.info.dark,
            },
          },
        },
        "&.MuiToggleButton-success": {
          color: theme.palette.success.main,
          borderColor: alpha(theme.palette.success.main, 0.5),
          "&:hover": {
            borderColor: theme.palette.success.main,
            backgroundColor: alpha(
              theme.palette.success.main,
              theme.palette.action.hoverOpacity,
            ),
          },
          "&.Mui-selected": {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
            borderColor: theme.palette.success.main,
            "&:hover": {
              backgroundColor: theme.palette.success.dark,
              borderColor: theme.palette.success.dark,
            },
          },
        },
        "&.MuiToggleButton-root.Mui-disabled": {
          color: theme.palette.action.disabled,
          borderColor: theme.palette.action.disabledBackground,
        },
        "&.MuiToggleButton-root.Mui-disabled.Mui-selected": {
          color: theme.palette.action.disabled,
          borderColor: theme.palette.action.disabledBackground,
          backgroundColor: theme.palette.action.disabledBackground,
        },
      }),
      sizeLarge: ({ theme }) => ({
        ...theme.typography.buttonLarge,
        padding: "8px 22px",
      }),
      sizeMedium: ({ theme }) => ({
        ...theme.typography.button,
        padding: "6px 16px",
      }),
      sizeSmall: ({ theme }) => ({
        ...theme.typography.buttonSmall,
        padding: "4px 10px",
      }),
    },
  },

  MuiIconButton: {
    styleOverrides: {
      sizeSmall: {
        fontSize: "1.125rem",
      },

      sizeMedium: {
        fontSize: "1.5rem",
      },

      sizeLarge: {
        fontSize: "1.75rem",
      },
    },
  },

  MuiSvgIcon: {
    styleOverrides: {
      fontSizeSmall: {
        fontSize: "1.25rem",
      },

      fontSizeMedium: {
        fontSize: "1.5rem",
      },

      fontSizeLarge: {
        fontSize: "2.1875rem",
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        paddingTop: 8,
        paddingRight: 24,
        paddingLeft: 24,

        "&:last-child": {
          paddingBottom: 16,
        },
      },
    },
  },

  MuiCardHeader: {
    styleOverrides: {
      root: {
        paddingBottom: 8,
        paddingLeft: 24,
        paddingRight: 24,
      },

      title: ({ theme }) => ({
        ...theme.typography.h6,
        padding: 0,
      }),

      subheader: ({ theme }) => ({
        ...theme.typography.body2,
        padding: 0,
      }),
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root: {
        svg: {
          height: 24,
          width: 24,

          "&.MuiSvgIcon-fontSizeSmall": {
            height: 20,
            width: 20,
          },
        },
      },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 16,
        paddingRight: 16,
        backgroundColor: theme.palette.blueGrey?.[50],
      }),
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        paddingBottom: 24,
        paddingLeft: 32,
        paddingRight: 32,
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        paddingTop: 24,
        paddingBottom: 16,
        paddingLeft: 32,
        paddingRight: 32,
      },
    },
  },

  MuiFormControl: {
    styleOverrides: {
      root: ({ theme }) => ({
        zIndex: theme.zIndex.layout.visible,
      }),
    },
  },

  MuiListItemText: {
    styleOverrides: {
      primary: {
        fontWeight: 500,
      },

      secondary: {
        fontSize: 13,
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      indicator: {
        transition: "none", // disable animated tab indicator transition
      },
    },
  },

  MuiTablePagination: {
    styleOverrides: {
      selectLabel: ({ theme }) => ({
        fontSize: 12,
        color: theme.palette.text.secondary,
        letterSpacing: 0.4,
      }),

      selectRoot: {
        fontSize: 12,
        letterSpacing: 0.4,

        "& .MuiSelect-select": {
          paddingTop: 5,
        },

        "& .MuiSvgIcon-root": {
          fontSize: "1.5rem",
        },
      },

      menuItem: {
        fontSize: 12,
      },

      displayedRows: {
        fontSize: 12,
        letterSpacing: 0.4,
      },

      actions: ({ theme }) => ({
        "& .MuiSvgIcon-root": {
          fontSize: "1.5rem",
        },

        "& .MuiButtonBase-root:not(.Mui-disabled)": {
          color: theme.palette.action.active,
        },
      }),
    },
  },

  MuiAvatar: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.blueGrey?.[50],
      }),
    },
  },
};

export default components;
